<template>
    <v-container class="fill-height" fluid>
        <v-row align="center" justify="center">
            <v-col cols="12" sm="8" md="5" lg="4" class="text-center">
                <v-avatar color="error" size="80" variant="tonal" class="mb-4">
                    <v-icon size="40">mdi-domain-off</v-icon>
                </v-avatar>
                <h1 class="text-h5 font-weight-bold mb-2">This circle is suspended</h1>
                <p class="text-body-2 text-medium-emphasis mb-6">
                    <template v-if="isOwner">
                        Your circle was suspended because of an outstanding balance.
                        Settle the invoice to restore tracking.
                    </template>
                    <template v-else>
                        This circle was suspended because of an outstanding balance.
                        The circle owner needs to settle the invoice to restore tracking.
                    </template>
                </p>

                <v-btn v-if="isOwner" color="primary" block class="mb-3"
                       prepend-icon="mdi-credit-card-outline" @click="goToBilling">
                    Open billing to pay
                </v-btn>

                <v-select v-if="otherCircles.length" v-model="switchTo" :items="otherCircles"
                          item-title="company_name" item-value="id" density="comfortable"
                          variant="outlined" label="Switch to another circle" hide-details
                          class="mb-3" @update:model-value="switchCircle"/>

                <v-btn variant="text" block class="mb-1" :loading="refreshing" @click="refresh">
                    I've paid - refresh
                </v-btn>
                <v-btn variant="text" block color="grey" @click="logout">Sign out</v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import {storeToRefs} from "pinia";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {send} from "@/composables/apiClient";

const router = useRouter();
const authStore = useAuthStore();
const {user} = storeToRefs(authStore);
// The store getter, NOT a local mirror of it. This screen is a full-screen
// intercept and the pay CTA is the only way off it, so if this disagreed with
// the getter a suspended OWNER would be trapped with no way to pay.
const isOwner = computed(() => authStore.isCircleOwner);
const {showSnackbar} = useSnackbarStore();

const refreshing = ref(false);
const switchTo = ref(null);

// Circles the user belongs to other than the suspended active one.
const otherCircles = computed(() => {
    const list = user.value?.companies ?? [];
    const activeId = user.value?.company?.id;
    return list.filter((c) => c.id !== activeId);
});

const goToBilling = () => router.push({name: "invoices"});

const refresh = async () => {
    refreshing.value = true;
    await authStore.hydrateProfile();
    refreshing.value = false;
    if (!authStore.circleSuspended) router.go();
};

const switchCircle = async (companyId) => {
    if (!companyId) return;
    try {
        await send("post", `switch-company/${companyId}`);
        await authStore.hydrateProfile();
        router.go();
    } catch (e) {
        showSnackbar(e.response?.data?.message ?? "Could not switch circle", "error");
    }
};

const logout = () => authStore.logout();
</script>
